import React from 'react';

const GENERAL_DISCLOSURE = 'This system is a Clinical Decision Support (CDS) tool and is not a regulated medical device. It assists healthcare professionals by highlighting trends in patient-reported data but does not provide medical diagnoses or treatment recommendations. Clinicians are responsible for all final diagnostic and treatment decisions.';

const CHART_CONTENT = {
  phq9: {
    title: 'PHQ-9 Tracker — Methodology & CDS Disclosure',
    logicSections: [
      {
        heading: 'Forecast Algorithm',
        body: 'Ordinary Least Squares (OLS) Linear Regression applied to the client\'s full PHQ-9 assessment history — days elapsed since the baseline assessment as the independent variable, PHQ-9 total score (0–27) as the dependent variable — projecting a predicted score at each of the next 8 weekly intervals and deriving a 4-week summary forecast point. Requires a minimum of 3 assessments to activate.'
      },
      {
        heading: 'Trend Classification',
        body: 'Directional comparison of the 3 most recent consecutive PHQ-9 scores. Strictly descending scores = "decreasing" (improving); strictly ascending = "increasing" (worsening). For non-monotonic patterns, the OLS slope of those same 3 points is computed: slope < −0.5 → "fluctuating, generally improving"; slope > +0.5 → "fluctuating, generally worsening"; |slope| ≤ 0.5 → "stable."'
      },
      {
        heading: 'Treatment Response Classification',
        body: 'Rule-based threshold classification derived from standardized depression outcome research: PHQ-9 score < 5 = Remission; ≥50% reduction from baseline score = Response; 25–49% reduction = Partial Response; all other presentations = Non-Response.'
      },
      {
        heading: 'Model Confidence (R²)',
        body: 'The R² coefficient of determination from the OLS regression fit, expressed as a percentage. Reflects the proportion of variance in observed PHQ-9 scores explained by the fitted linear trend line. This is a statistical fit metric — it does not represent the probability of a clinical outcome.'
      },
      {
        heading: 'Rate of Change',
        body: 'Calculated as (current score − baseline score) ÷ total days elapsed × 30. Expressed as estimated PHQ-9 points of change per month.'
      }
    ],
    predictionGoal: 'Projected PHQ-9 score at each of the next 8 weekly intervals and at 4 weeks; treatment response trajectory classification (Response, Partial Response, Remission, or Non-Response).',
    citations: [
      'Kroenke K, Spitzer RL, Williams JB. "The PHQ-9: validity of a brief depression severity measure." J Gen Intern Med. 2001;16(9):606-613.',
      'American Psychiatric Association. Diagnostic and Statistical Manual of Mental Disorders, Fifth Edition, Text Revision (DSM-5-TR). Washington, DC: APA; 2022.'
    ],
    clinicianRole: [
      'This output is a supplemental summary only — it does not constitute a diagnosis or a treatment recommendation.',
      'The clinician must manually review all raw PHQ-9 item scores, specifically Item 9 (thoughts of death or self-harm), at every clinical encounter regardless of the total score trend.',
      'Forecast projections assume a linear rate of change and must be interpreted in the context of the client\'s full clinical picture, including recent life events and medication changes.',
      'Clinical judgment is required to confirm or override any trend classification before altering a treatment plan.'
    ],
    limitations: [
      'Forecasting is disabled when fewer than 3 PHQ-9 assessments are available.',
      'OLS Linear Regression assumes a consistent, unidirectional rate of change; non-linear recovery curves (e.g., acute crisis followed by rapid improvement) will reduce forecast accuracy.',
      'R² model confidence reflects fit to historical data — not predictive certainty for future scores.',
      'The tool does not capture qualitative clinical information (life events, medication adjustments, comorbid conditions) that may significantly affect score trajectory.'
    ]
  },
  gad7: {
    title: 'GAD-7 Tracker — Methodology & CDS Disclosure',
    logicSections: [
      {
        heading: 'Forecast Algorithm',
        body: 'Ordinary Least Squares (OLS) Linear Regression applied to the client\'s full GAD-7 assessment history — days elapsed since the baseline assessment as the independent variable, GAD-7 total score (0–21) as the dependent variable — projecting a predicted score at each of the next 8 weekly intervals and deriving a 4-week summary forecast point. Requires a minimum of 3 assessments to activate.'
      },
      {
        heading: 'Trend Classification',
        body: 'Directional comparison of the 3 most recent consecutive GAD-7 scores. Strictly descending scores = "decreasing" (improving); strictly ascending = "increasing" (worsening). For non-monotonic patterns, the OLS slope of those same 3 points is computed: slope < −0.5 → "fluctuating, generally improving"; slope > +0.5 → "fluctuating, generally worsening"; |slope| ≤ 0.5 → "stable."'
      },
      {
        heading: 'Statistical Deviation from Baseline',
        body: 'The rate of change metric (points per month) and the overall delta (current score − baseline score) together quantify the degree of statistical deviation from the client\'s established baseline, identifying whether anxiety symptom burden is accelerating, decelerating, or stable relative to the starting point.'
      },
      {
        heading: 'Treatment Response Classification',
        body: 'Rule-based threshold classification: GAD-7 score < 5 = Remission; ≥50% reduction from baseline = Response; 25–49% reduction = Partial Response; all other presentations = Non-Response.'
      },
      {
        heading: 'Model Confidence (R²)',
        body: 'The R² coefficient of determination from the OLS regression fit, expressed as a percentage. Reflects the proportion of variance in observed GAD-7 scores explained by the fitted linear trend line — not a probability of clinical outcome.'
      }
    ],
    predictionGoal: 'Projected GAD-7 score at each of the next 8 weekly intervals and at 4 weeks; degree of deviation from established anxiety symptom baseline; treatment response trajectory classification (Response, Partial Response, Remission, or Non-Response).',
    citations: [
      'Spitzer RL, Kroenke K, Williams JB, et al. "A brief measure for assessing generalized anxiety disorder: the GAD-7." Arch Intern Med. 2006;166(10):1092-1097.',
      'Pfizer Inc. Instruction Manual: Instructions for Patient Health Questionnaire (PHQ) and GAD-7 Measures. New York, NY: Pfizer Inc.; 2010.'
    ],
    clinicianRole: [
      'This tool provides data visualization and statistical pattern recognition — it does not constitute a diagnosis or treatment recommendation.',
      'The clinician must verify the accuracy of underlying GAD-7 data points and review all raw item scores before drawing clinical conclusions.',
      'External stressors, life events, and context not captured by the GAD-7 score must be considered before making any diagnostic or prescriptive decisions.',
      'Forecast projections assume a linear trajectory and must be interpreted alongside the full clinical presentation.'
    ],
    limitations: [
      'Forecasting is disabled when fewer than 3 GAD-7 assessments are available.',
      'OLS Linear Regression assumes a consistent rate of change; episodic anxiety patterns driven by discrete stressors may not be well-represented by a linear model.',
      'R² model confidence reflects fit to historical data — not predictive certainty for future scores.',
      'The tool does not capture situational or environmental factors (e.g., acute stressors, substance use, sleep disruption) that frequently drive GAD-7 fluctuations.'
    ]
  },
  noshow: {
    title: 'No-Show Analysis — Methodology & CDS Disclosure',
    logicSections: [
      {
        heading: 'Risk Tier Classification',
        body: 'Rule-based threshold classification applied to each client\'s no-show percentage: < 25% = Low Risk (acceptable attendance); 25–34% = Moderate Risk (concerning pattern); 35–50% = High Risk (significant attendance barrier); > 50% = Critical (chronic disengagement). These thresholds are operational practice management tiers informed by published attendance research — they are not clinically-validated diagnostic cutpoints.'
      },
      {
        heading: 'Practice No-Show Rate',
        body: 'Calculated as: (total no-shows across all clients) ÷ (total scheduled appointments across all clients) × 100. This aggregate rate reflects overall caseload attendance burden and is not adjusted for clinical acuity, diagnosis mix, or practice setting.'
      },
      {
        heading: 'Client Triage Categories',
        body: 'Clients are ranked by descending no-show percentage to prioritize outreach. The triage categories (Critical, High Risk, Moderate Risk, Low Risk) support operational engagement decisions — they do not represent a clinical assessment of the client\'s mental health status or treatment prognosis.'
      }
    ],
    predictionGoal: 'Identification of clients with elevated no-show rates for prioritized re-engagement; practice-level attendance summary to support operational quality monitoring.',
    citations: [
      'DeFife JA, Conklin CZ, Smith JM, Poole J. Psychotherapy appointment no-shows: rates and reasons. Psychotherapy (Chic). 2010 Sep;47(3):413-417. doi: 10.1037/a0021168. PMID: 22402096.',
      'Dantas LF, Fleck JL, Cyrino Oliveira FL, Hamacher S. No-shows in appointment scheduling - a systematic literature review. Health Policy. 2018 Apr;122(4):412-421. doi: 10.1016/j.healthpol.2018.02.002. Epub 2018 Feb 15. PMID: 29482948.'
    ],
    clinicianRole: [
      'No-show rates are one operational indicator — they must be interpreted alongside clinical context, including the client\'s diagnosis, life circumstances, and therapeutic alliance.',
      'A high no-show rate does not indicate treatment failure or clinical deterioration; clinicians should conduct a direct engagement conversation before drawing clinical conclusions.',
      'Re-engagement outreach and barrier assessment (e.g., transportation, scheduling conflicts, stigma, financial access) should precede any treatment plan modification based on attendance data.',
      'Clinicians retain full responsibility for determining whether a client\'s attendance pattern warrants escalation, case closure, or a modified care plan.'
    ],
    limitations: [
      'The tool relies entirely on attendance data entered by the clinician — accuracy depends on complete and consistent data entry.',
      'No-show thresholds are based on published population-level attendance research and may not reflect expected attendance patterns for specific client populations or practice settings.',
      'The analysis does not distinguish between no-shows due to client disengagement and those resulting from provider-initiated cancellations, emergencies, or scheduling errors.',
      'No causal inference can be drawn from attendance patterns alone; attendance rate is a proxy metric, not a measure of clinical engagement or therapeutic outcome.'
    ]
  }
};

const CDSInfoModal = ({ isOpen, onClose, chartType = 'phq9' }) => {
  if (!isOpen) return null;

  const content = CHART_CONTENT[chartType];
  if (!content) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[88vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-base font-bold text-gray-900">{content.title}</h2>
            <p className="text-xs text-blue-600 mt-0.5 font-medium">
              21st Century Cures Act · CDS Transparency Disclosure (Criterion 4)
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-4 text-gray-400 hover:text-gray-700 text-2xl leading-none flex-shrink-0"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto px-6 py-5 space-y-5 text-sm flex-1">

          {/* General Disclosure */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-xs font-bold text-yellow-700 uppercase tracking-wide mb-1">
              General CDS Disclosure
            </p>
            <p className="text-yellow-900 leading-relaxed">{GENERAL_DISCLOSURE}</p>
          </div>

          {/* Logic Mechanism */}
          <div>
            <p className="font-bold text-gray-900 mb-3">Logic Mechanism</p>
            <div className="space-y-3">
              {content.logicSections.map((section, i) => (
                <div key={i} className="pl-3 border-l-2 border-blue-300">
                  <p className="font-semibold text-gray-800 mb-0.5">{section.heading}</p>
                  <p className="text-gray-600 leading-relaxed">{section.body}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Prediction Goal */}
          <div>
            <p className="font-bold text-gray-900 mb-1">Prediction Goal</p>
            <p className="text-gray-600 leading-relaxed pl-3 border-l-2 border-blue-300">{content.predictionGoal}</p>
          </div>

          {/* Sources */}
          <div>
            <p className="font-bold text-gray-900 mb-2">Sources & Citations</p>
            <ul className="space-y-2">
              {content.citations.map((cite, i) => (
                <li key={i} className="pl-3 border-l-2 border-gray-300 text-gray-600 leading-relaxed">
                  {cite}
                </li>
              ))}
            </ul>
          </div>

          {/* Clinician Role */}
          <div>
            <p className="font-bold text-gray-900 mb-2">Clinician Role</p>
            <ul className="space-y-2">
              {content.clinicianRole.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-gray-700">
                  <span className="text-blue-500 font-bold mt-0.5 flex-shrink-0">·</span>
                  <span className="leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Known Limitations */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="font-bold text-gray-800 mb-2">Known Limitations</p>
            <ul className="space-y-2">
              {content.limitations.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-gray-600">
                  <span className="text-amber-500 flex-shrink-0 mt-0.5">⚠</span>
                  <span className="leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>

        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-xl">
          <button
            onClick={onClose}
            className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold text-sm transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CDSInfoModal;
